// מצב החיבור לשרת, אחד לכל האפליקציה (12 §1 — deploy בלי השבתה). מי שמדבר עם השרת מדווח: fetchRetrying (updating.ts)
// על כל תשובה/כשל, openSse על רמז הניקוז ("server is restarting") ועל חיבור שנפתח. ConnectionPill מציג.
// שלושה סוגים, כי המשתמש צריך לדעת של מי הבעיה:
//   updating — 502/503/504 או ניקוז SSE: החלפת גרסה מתוכננת, חוזרים לבד.
//   offline  — (globalThis as { navigator?: { onLine?: boolean } }).navigator?.onLine false: הרשת אצל המשתמש.
//   error    — הרשת אצלו תקינה אבל השרת לא עונה (חיבור נכשל) או עונה 5xx לבדיקה.
// רגוע בכוונה: חסד של GRACE_MS לפני שמציגים (ניקוז רגיל נגמר בחצי שנייה — לא מהבהבים), "חזרנו" רק אם הוצג משהו,
// בלי מונים; אחרי LONG_MS — ניסוח של "לוקח יותר מהרגיל". כשאין בקשה שתגלה שחזרנו — בדיקה קלה ברקע (probe).
import { useSyncExternalStore } from 'react';

export type Trouble = 'updating' | 'offline' | 'error';
export type Health =
  | { phase: 'ok' }
  | { phase: 'trouble'; kind: Trouble; long: boolean }
  | { phase: 'back' };

export const GRACE_MS = 1500;
export const LONG_MS = 120_000;
export const BACK_MS = 2400;
/** בדיקה שהשרת עונה: GET בלי token → 401 מהיר מה-api (כל תשובה < 500 = עונה); nginx עונה 503 כשה-api מתחלף. */
export const PROBE_PATH = '/api/artifacts';

const hasDom = typeof (globalThis as { addEventListener?: unknown }).addEventListener === 'function';
let kind: Trouble | null = null;
let shown = false;
let long = false;
let back = false;
let graceT: ReturnType<typeof setTimeout> | null = null;
let longT: ReturnType<typeof setTimeout> | null = null;
let backT: ReturnType<typeof setTimeout> | null = null;
let probeT: ReturnType<typeof setTimeout> | null = null;
let probeN = 0;
let snap: Health = { phase: 'ok' };
const subs = new Set<() => void>();

function emit() {
  const next: Health = shown && kind ? { phase: 'trouble', kind, long } : back ? { phase: 'back' } : { phase: 'ok' };
  if (next.phase === snap.phase && (next.phase !== 'trouble' || (snap.phase === 'trouble' && next.kind === snap.kind && next.long === snap.long))) return;
  snap = next;
  subs.forEach((f) => f());
}

const clear = (t: ReturnType<typeof setTimeout> | null) => t && clearTimeout(t);

// offline גובר (הרשת אצל המשתמש), אחריו updating (ידוע שזה עדכון), ו-error רק כשאין הסבר טוב יותר
const RANK: Record<Trouble, number> = { error: 0, updating: 1, offline: 2 };

/** משהו לא עבד: השרת לא ענה / ענה שהוא מתחלף / אין רשת. */
export function reportTrouble(k: Trouble): void {
  if (k !== 'offline' && hasDom && !(globalThis as { navigator?: { onLine?: boolean } }).navigator?.onLine) k = 'offline';
  if (kind && RANK[k] < RANK[kind] && kind !== 'offline') {
    schedProbe();
    return;
  }
  const first = !kind;
  kind = k;
  if (first) {
    back = false;
    clear(backT);
    graceT = setTimeout(() => {
      shown = true;
      emit();
    }, GRACE_MS);
    longT = setTimeout(() => {
      long = true;
      emit();
    }, LONG_MS);
    probeN = 0;
  }
  schedProbe();
  emit();
}

/** השרת ענה (כל תשובה שאינה 502/503/504) — אם הייתה תקלה, היא נגמרה. */
export function reportOk(): void {
  if (!kind) return;
  const wasShown = shown;
  kind = null;
  shown = false;
  long = false;
  clear(graceT);
  clear(longT);
  clear(probeT);
  probeT = null;
  if (wasShown) {
    back = true;
    backT = setTimeout(() => {
      back = false;
      emit();
    }, BACK_MS);
  }
  emit();
}

function schedProbe() {
  if (!hasDom || probeT || !kind) return;
  // 2s, 3s, 5s, 8s, 10s… — לא מציפים שרת שעולה
  const ms = Math.min(10_000, [2000, 3000, 5000, 8000][probeN] ?? 10_000);
  probeN++;
  probeT = setTimeout(() => {
    probeT = null;
    void probe();
  }, ms);
}

async function probe() {
  if (!kind) return;
  if (!(globalThis as { navigator?: { onLine?: boolean } }).navigator?.onLine) {
    reportTrouble('offline');
    return;
  }
  try {
    const r = await fetch(PROBE_PATH, { cache: 'no-store', signal: AbortSignal.timeout(8000) });
    if (r.status === 502 || r.status === 503 || r.status === 504) reportTrouble('updating');
    else if (r.status >= 500) reportTrouble('error');
    else reportOk();
  } catch {
    reportTrouble((globalThis as { navigator?: { onLine?: boolean } }).navigator?.onLine ? 'error' : 'offline');
  }
}

if (hasDom) {
  (globalThis as unknown as { addEventListener(t: string, f: () => void): void }).addEventListener('offline', () => reportTrouble('offline'));
  (globalThis as unknown as { addEventListener(t: string, f: () => void): void }).addEventListener('online', () => {
    // הרשת חזרה — עוד לא ידוע אם השרת עונה: בודקים מיד במקום לחכות לתור
    if (kind !== 'offline') return;
    kind = 'error'; // זמני עד שהבדיקה תכריע; לא נראה למשתמש אם תוך רגע חזרנו
    clear(probeT);
    probeT = null;
    probeN = 0;
    void probe();
  });
}

export function getHealth(): Health {
  return snap;
}

export function useHealth(): Health {
  return useSyncExternalStore(
    (f) => {
      subs.add(f);
      return () => void subs.delete(f);
    },
    getHealth,
  );
}

/** לבדיקות בלבד: איפוס מלא. */
export function resetHealthForTests(): void {
  kind = null;
  shown = back = long = false;
  [graceT, longT, backT, probeT].forEach(clear);
  probeT = null;
  snap = { phase: 'ok' };
}
